// **تذكيرُ الحضور والانصراف في وقتهما** (قرار المالك 23/09/2026: «الحضور لوحده والانصراف لوحده،
// وفكّره في الحالتين: للحضور في أوّل اليوم اعمل تنبيه تذكّره، والانصراف قبل ما ييجي وقته بـ10
// دقايق فكّره بالانصراف»).
//
// دالّةٌ خالصة تُختبر وحدها: تأخذ حالة اليوم والجدول ولحظةً، وتقول أيّ تذكيرٍ مستحقّ الآن. والشاشة
// تعرضه مرّةً واحدة في اليوم لكل نوع (تحفظ ذلك في المتصفّح) — فلا يتحوّل التذكير إلى إزعاج.
//
//   • **الحضور**: لم يُسجَّل حضورٌ بعد، واليوم ليس إجازة ولم ينتهِ، وقد بدأ الدوام (الجدول الثابت)
//     أو أيَّ وقتٍ من اليوم (المرن — لا بداية له).
//   • **الانصراف**: جلسةٌ مفتوحة، والجدولُ ثابت، وبقي على نهايته عشرُ دقائق أو أقلّ. والمرنُ لا
//     نهاية له تُعرف، فلا تذكيرَ انصراف فيه — صاحبُه مسؤولٌ عن وقته (قرار المالك في رحمه).
import type { AttendanceToday } from '../lib/api.ts';
import { cairoParts } from '../lib/format.ts';
import { isOpenSession } from './TapButtons.tsx';

export type NudgeKind = 'CHECK_IN' | 'CHECK_OUT';
export interface Nudge { kind: NudgeKind; text: string }

export const CHECK_OUT_LEAD_MINUTES = 10;

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)/;
const minutesOf = (hhmm?: string): number | null => {
  const m = hhmm ? HHMM.exec(hhmm) : null;
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const daysBetween = (a: string, b: string): number =>
  Math.round((Date.UTC(+b.slice(0, 4), +b.slice(5, 7) - 1, +b.slice(8, 10))
    - Date.UTC(+a.slice(0, 4), +a.slice(5, 7) - 1, +a.slice(8, 10))) / 86_400_000);

export function nudgeFor(today: AttendanceToday | null | undefined, now: Date = new Date()): Nudge | null {
  if (!today || today.dayIsFinal || today.recordStatus === 'ON_LEAVE') return null;
  const p = cairoParts(now);
  const sinceMidnight = daysBetween(today.anchorDate, p.date) * 1440 + p.hour * 60 + p.minute;
  const s = today.schedule;
  const start = s.kind === 'FIXED' ? minutesOf(s.startLocal) : null;
  const endRaw = s.kind === 'FIXED' ? minutesOf(s.endLocal) : null;
  const end = endRaw === null ? null : endRaw + (s.crossesMidnight ? 1440 : 0);

  if (!today.checkedIn && (s.kind === 'FLEXIBLE' || (start !== null && sinceMidnight >= start))) {
    return { kind: 'CHECK_IN', text: 'بداية يومك — متنساش تسجّل الحضور' };
  }
  if (isOpenSession(today) && end !== null && sinceMidnight >= end - CHECK_OUT_LEAD_MINUTES) {
    return { kind: 'CHECK_OUT', text: `فاضل ${CHECK_OUT_LEAD_MINUTES} دقايق على الانصراف — متنساش تسجّل الانصراف` };
  }
  return null;
}

const key = (anchorDate: string, kind: NudgeKind): string => `icontravel.nudge.${anchorDate}.${kind}`;
export function nudgeShown(anchorDate: string, kind: NudgeKind): boolean {
  try { return localStorage.getItem(key(anchorDate, kind)) === '1'; } catch { return false; }
}
export function markNudgeShown(anchorDate: string, kind: NudgeKind): void {
  try { localStorage.setItem(key(anchorDate, kind), '1'); } catch { /* تصفّحٌ خاص: يظهر مرّةً في الجلسة */ }
}
